import { useContext, useState } from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { registerToExistingOrg, registerToNewOrg } from "../../api";
import {
	BlockInput,
	DataForm,
	Preloader,
	handleDataFormSubmit,
	setDataFormField,
} from "../../components";
import { FieldType, modalActions, routes } from "../../constants";
import { UserDataContext } from "../../contexts/userDataContext";
import { formatUserNameWithEmailString } from "../../utils";
import { setNewOrgIdLS } from "../../utils/localStorageUtils";

const createOrgSetting = {
	action: modalActions.CREATE,
	isVerticalLayout: true,
	fields: [
		[
			{
				title: "Create New Organization",
				placeholder: "New Organization name",
				field: "newOrgName",
				isMandatory: true,
			},
		],
	],
};

export const RegisterSubToOrgExisting = () => {
	let { invitationId, email: emailEncoded } = useParams<{
		invitationId: string;
		email: string;
	}>();
	const { userOrgs, userProfile } =
		useContext(UserDataContext);

	const [currentOrgFormData, setOrgFormData] = useState({
		newOrgName: "",
		orgId: "",
	});
	const [orgFormErrors, setOrgFormErrors] = useState({});

	const [isLoading, setIsLoading] = useState(false);
	const [isPreloader, setPreloader] = useState(false);

	const selectOrgSetting = {
		action: modalActions.CREATE,
		isVerticalLayout: true,
		fields: [
			[
				{
					title: "Select Organization",
					placeholder: "Select Organization",
					field: "orgId",
					type: FieldType.DROPDOWN,
					dropdownData: [...userOrgs]
						.sort((orgA, orgB) =>
							orgA.name.localeCompare(orgB.name)
						)
						.map((org) => ({
							id: org.id,
							label: org.name,
						})),
					isMandatory: true,
				},
			],
		],
	};

	const handleRegisterCreate = () =>
		handleDataFormSubmit({
			data: currentOrgFormData,
			setting: createOrgSetting,
			handleSuccess: async () => {
				try {
					setIsLoading(true);
					setPreloader(true);

					const response = await registerToNewOrg({
						invitationId,
						newOrgName: currentOrgFormData.newOrgName,
					});

					if (response.orgId) {
						setNewOrgIdLS(response.orgId);
					}
					window.location.href = "/";
				} catch (error) {
					console.error(error);
				} finally {
					setIsLoading(false);
					setPreloader(false);
				}
			},
			handleError: setOrgFormErrors,
		});

	const handleLoginSubscribe = () =>
		handleDataFormSubmit({
			data: currentOrgFormData,
			setting: selectOrgSetting,
			handleSuccess: async () => {
				try {
					setIsLoading(true);
					setPreloader(true);
					await registerToExistingOrg({
						invitationId,
						orgId: currentOrgFormData.orgId,
					});
					setNewOrgIdLS(currentOrgFormData.orgId);
					window.location.href = "/";
				} catch (error) {
					console.error(error);
					toast.error("Something went wrong!");
				} finally {
					setIsLoading(false);
					setPreloader(false);
				}
			},
			handleError: setOrgFormErrors,
		});

	return (
		<>
			<Preloader show={isPreloader} />
			<main>
				<section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
					<Container>
						<p className="text-center">
							<Card.Link
								as={Link}
								to={`${routes.registerSubscription.path}/${emailEncoded}/${invitationId}`}
								className="text-gray-700"
							>
								<FontAwesomeIcon
									icon={faAngleLeft}
									className="me-2"
								/>
								Back
							</Card.Link>
						</p>
						<div className="text-center text-md-center mb-4 mt-md-0">
							<h3 className="mb-0">
								Welcome back{" "}
								{formatUserNameWithEmailString(userProfile)}
							</h3>
						</div>
						<Row className="justify-content-center form-bg-image">
							{userOrgs.length > 0 ? (
								<>
									<Col
										xs={12}
										lg={6}
										className="d-flex align-items-center justify-content-center"
									>
										<div
											style={{ position: "relative" }}
											className="mb-4 mb-lg-0 bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500"
										>
											<BlockInput blocking={isLoading} />
											<div className="text-center text-md-center mb-4 mt-md-0">
												<h3 className="mb-0">
													Add Subscription to Existing
													Organization
												</h3>
											</div>
											<DataForm
												className="mt-4"
												currentFormData={
													currentOrgFormData
												}
												setFormData={setDataFormField(
													setOrgFormData
												)}
												onSubmit={handleLoginSubscribe}
												editorSetting={selectOrgSetting}
												errors={orgFormErrors}
											/>
											<Button
												disabled={isLoading}
												variant="primary"
												type="submit"
												className="w-100"
												onClick={handleLoginSubscribe}
											>
												Add Subscription to Existing
												Organization
											</Button>
										</div>
									</Col>
								</>
							) : null}
							<Col
								xs={12}
								lg={6}
								className="d-flex align-items-center justify-content-center"
							>
								<div
									style={{ position: "relative" }}
									className="mb-4 mb-lg-0 bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500"
								>
									<BlockInput blocking={isLoading} />
									<div className="text-center text-md-center mb-4 mt-md-0">
										<h3 className="mb-0">
											Add Subscription to New Organization
										</h3>
									</div>
									<DataForm
										className="mt-4"
										currentFormData={currentOrgFormData}
										setFormData={setDataFormField(
											setOrgFormData
										)}
										onSubmit={handleRegisterCreate}
										editorSetting={createOrgSetting}
										errors={orgFormErrors}
									/>
									<Button
										disabled={isLoading}
										variant="primary"
										type="submit"
										className="w-100"
										onClick={handleRegisterCreate}
									>
										Add Subscription to New Organization
									</Button>
								</div>
							</Col>
						</Row>
					</Container>
				</section>
			</main>
		</>
	);
};
